import React, { useEffect, useState, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Box, MenuItem, Typography,
  CircularProgress, Alert, Chip,
} from '@mui/material';
import { UploadFile as UploadIcon } from '@mui/icons-material';
import apiClient from '../../api/client';
import { Project, Client, ProjectType, ProjectStatus } from '../../types';

const PROJECT_TYPES: ProjectType[] = ['Fixed Price', 'Retainer', 'Milestone-Based'];
const PROJECT_STATUSES: { value: ProjectStatus; label: string }[] = [
  { value: 'active',    label: 'Active' },
  { value: 'on_hold',   label: 'On Hold' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: (project: Project) => void;
  project: Project | null;
}

interface FormData {
  project_name: string;
  client_id: string;
  project_type: ProjectType;
  status: ProjectStatus;
  revenue: string;
  start_date: string;
  end_date: string;
  notes: string;
}

const initialForm: FormData = {
  project_name: '',
  client_id: '',
  project_type: 'Fixed Price',
  status: 'active',
  revenue: '',
  start_date: '',
  end_date: '',
  notes: '',
};

export default function ProjectFormDialog({ open, onClose, onSaved, project }: Props) {
  const [form, setForm] = useState<FormData>(initialForm);
  const [clients, setClients] = useState<Client[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // SOW file
  const [sowFile, setSowFile] = useState<File | null>(null);
  const [sowFilename, setSowFilename] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    apiClient.get('/clients')
      .then(r => setClients(r.data.filter((c: Client) => c.status === 'active')))
      .catch(console.error);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (project) {
      setForm({
        project_name: project.project_name,
        client_id:    project.client_id,
        project_type: project.project_type,
        status:       project.status,
        revenue:      String(project.revenue),
        start_date:   project.start_date ? project.start_date.slice(0, 10) : '',
        end_date:     project.end_date   ? project.end_date.slice(0, 10)   : '',
        notes:        project.notes || '',
      });
      setSowFilename(project.sow_filename);
    } else {
      setForm(initialForm);
      setSowFilename(null);
    }
    setSowFile(null);
    setError(null);
  }, [open, project]);

  const set = (field: keyof FormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm(prev => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async () => {
    if (!form.project_name.trim()) { setError('Project name is required.'); return; }
    if (!form.client_id)           { setError('Please select a client.'); return; }

    setSaving(true);
    setError(null);

    try {
      let sow_base64: string | undefined;
      let sow_filename: string | undefined;

      if (sowFile) {
        sow_base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(sowFile);
        });
        sow_filename = sowFile.name;
      }

      const payload = {
        project_name: form.project_name.trim(),
        client_id:    form.client_id,
        project_type: form.project_type,
        status:       form.status,
        revenue:      parseFloat(form.revenue) || 0,
        notes:        form.notes.trim() || null,
        start_date:   form.start_date || null,
        end_date:     form.end_date   || null,
        ...(sow_base64 ? { sow_base64, sow_filename } : {}),
      };

      let saved: Project;
      if (project) {
        const { data } = await apiClient.put(`/projects/${project.id}`, payload);
        saved = { ...data, client_name: clients.find(c => c.id === form.client_id)?.client_name || project.client_name };
      } else {
        const { data } = await apiClient.post('/projects', payload);
        saved = { ...data, client_name: clients.find(c => c.id === form.client_id)?.client_name || '' };
      }

      onSaved(saved);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to save project. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const fieldSx = { mb: 2 };
  const rowSx   = { display: 'flex', gap: 2, mb: 2 };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ pb: 1, fontWeight: 700 }}>
        {project ? 'Edit Project' : 'Add New Project'}
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Project Name */}
        <TextField label="Project Name" value={form.project_name} onChange={set('project_name')}
          fullWidth size="small" required sx={fieldSx} />

        {/* Client + Type */}
        <Box sx={rowSx}>
          <TextField select label="Client" value={form.client_id} onChange={set('client_id')}
            fullWidth size="small" required>
            <MenuItem value=""><em>Select client</em></MenuItem>
            {clients.map(c => <MenuItem key={c.id} value={c.id}>{c.client_name}</MenuItem>)}
          </TextField>
          <TextField select label="Project Type" value={form.project_type} onChange={set('project_type')}
            fullWidth size="small">
            {PROJECT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
        </Box>

        {/* Status + Revenue */}
        <Box sx={rowSx}>
          <TextField select label="Status" value={form.status} onChange={set('status')}
            fullWidth size="small">
            {PROJECT_STATUSES.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
          </TextField>
          <TextField label="Revenue ($)" value={form.revenue} onChange={set('revenue')}
            fullWidth size="small" type="number" inputProps={{ min: 0, step: '0.01' }}
            placeholder="0.00" />
        </Box>

        {/* Start + End Date */}
        <Box sx={rowSx}>
          <TextField label="Start Date" value={form.start_date} onChange={set('start_date')}
            fullWidth size="small" type="date" InputLabelProps={{ shrink: true }} />
          <TextField label="End Date" value={form.end_date} onChange={set('end_date')}
            fullWidth size="small" type="date" InputLabelProps={{ shrink: true }} />
        </Box>

        {/* Notes */}
        <TextField label="Notes" value={form.notes} onChange={set('notes')}
          fullWidth size="small" multiline rows={3}
          placeholder="Scope, deliverables, or any relevant notes…"
          sx={fieldSx} />

        {/* SOW Upload */}
        <Box>
          <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 600,
            textTransform: 'uppercase', fontSize: 11, letterSpacing: '0.06em', color: 'text.secondary' }}>
            Statement of Work (SOW)
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Button variant="outlined" size="small" startIcon={<UploadIcon />}
              onClick={() => fileInputRef.current?.click()}
              sx={{ textTransform: 'none', borderStyle: 'dashed' }}>
              {sowFile ? 'Change file' : sowFilename ? 'Replace file' : 'Upload SOW'}
            </Button>
            {(sowFile || sowFilename) && (
              <Chip size="small" label={sowFile ? sowFile.name : sowFilename || ''}
                onDelete={() => { setSowFile(null); setSowFilename(null); }}
                sx={{ maxWidth: 240, fontSize: 12 }} />
            )}
            <input ref={fileInputRef} type="file" hidden accept=".pdf,.doc,.docx"
              onChange={e => setSowFile(e.target.files?.[0] || null)} />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            PDF, DOC, or DOCX — max 5 MB
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={saving}
          sx={{ minWidth: 110, fontWeight: 600 }}>
          {saving ? <CircularProgress size={18} color="inherit" /> : project ? 'Save Changes' : 'Create Project'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
